/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo, useCallback, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Stack } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import { SERVICE_TYPES } from 'shared';

import { PageOverlay } from '../../components/PageOverlay/PageOverlay';
import { RootState } from '../../store';
import { closeCreateServiceOverlay } from '../../store/createService/actions';
import { ServiceCard } from './ServiceCard';

const mapState = (state: RootState) => ({
  createService: state.createService,
});

const connector = connect(mapState);
type PropsFromRedux = ConnectedProps<typeof connector>;

const createStyles = makeStyles(() => ({
  modal: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0 24px',
    width: 'auto',
    maxWidth: 'none',
  },
}));

const CreateServiceOverlayComponent = memo<PropsFromRedux>(props => {
  const classes = createStyles();

  const { createService, dispatch } = props;
  const { isOpen } = createService;

  const matchParams = useMemo(
    () => ({
      organizationId: createService.organizationId,
      projectId: createService.projectId,
      clusterId: createService.clusterId,
    }),
    [createService],
  );

  const close = useCallback(() => {
    dispatch(closeCreateServiceOverlay());
  }, [dispatch]);

  return (
    <PageOverlay
      open={isOpen}
      classes={{ childrenBlock: 'd-flex', modal: classes.modal }}
      disableRestoreFocus
      name="create-service"
      sx={{
        transition: 'all 2s ease-in-out',
      }}
    >
      {isOpen ? (
        <>
          <Box sx={{ position: 'fixed', top: '38px' }}>
            <IconButton
              aria-label="close"
              size="medium"
              color="primary"
              onClick={close}
              data-cy="page-overlay-close-button"
            >
              <CloseIcon color="primary" />
            </IconButton>
          </Box>
          <Stack direction="row" gap={3}>
            {SERVICE_TYPES.map(type => (
              <ServiceCard key={type} serviceType={type} matchParams={matchParams} />
            ))}
          </Stack>
        </>
      ) : null}
    </PageOverlay>
  );
});

export const CreateServiceOverlay = connector(CreateServiceOverlayComponent);
